import { skipToken, useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { Suspense, use, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { AuthDispatchContext } from '@/components/auth/context'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SELECTED_COMPANY_KEY } from '@/config/local-storage'
import { SUBSCRIPTION_CANCELLED_KEY } from '@/config/session-storage'
import {
  useCompanyPath,
  useIdCompany,
  useMixpanel,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { BlockedState } from '@/routes/billing/danger-zone/delete-company-sheet/blocked-state'
import { ConfirmStep } from '@/routes/billing/danger-zone/delete-company-sheet/confirm-step'
import { DeleteCompanySheetFallback } from '@/routes/billing/danger-zone/delete-company-sheet/fallback'
import {
  CANCEL_SEVALLA_ACCOUNT_MUTATION,
  DELETE_COMPANY_SHEET_QUERY,
} from '@/routes/billing/danger-zone/delete-company-sheet/query'
import { ReasonStep } from '@/routes/billing/danger-zone/delete-company-sheet/reason-step'
import { deleteCompanySchema } from '@/routes/billing/danger-zone/delete-company-sheet/reasons'
import type {
  CancelSevallaAccountMutation,
  CancelSevallaAccountMutationVariables,
  DeleteCompanySheetQuery,
  DeleteCompanySheetQueryVariables,
} from '@/routes/billing/danger-zone/delete-company-sheet/query.types.gen'
import type { DeleteCompanyFormValues } from '@/routes/billing/danger-zone/delete-company-sheet/reasons'

type Props = {
  isOpen: boolean
  onClose: () => void
}

type Step = 'reason' | 'confirm'

const STEP_DESCRIPTIONS: Record<Step, string> = {
  reason: 'Step 1 of 2 — Tell us why you are leaving',
  confirm: 'Step 2 of 2 — Review and confirm',
}

const Content = ({ isOpen, onClose }: Props) => {
  const [isPending, startTransition] = useTransition()
  const [step, setStep] = useState<Step>('reason')
  const { idCompany } = useIdCompany()
  const { totalServices } = useRBAC()
  const { navigateToPath } = useCompanyPath()
  const { track } = useMixpanel()
  const { logout } = use(AuthDispatchContext) ?? {}

  const { data } = useSuspenseQuery<
    DeleteCompanySheetQuery,
    DeleteCompanySheetQueryVariables
  >(DELETE_COMPANY_SHEET_QUERY, isOpen ? {} : skipToken)
  const [cancelSevallaAccount] = useMutation<
    CancelSevallaAccountMutation,
    CancelSevallaAccountMutationVariables
  >(CANCEL_SEVALLA_ACCOUNT_MUTATION)

  const companies = data?.user?.companies ?? []
  const activeCompany = companies.find((company) => company?.id === idCompany)
  const isOnlyCompany = companies.length === 1

  const totalRemainingServices = Object.values(totalServices).reduce(
    (sum, total) => sum + total,
    0
  )
  const isBlocked = totalRemainingServices > 0

  const form = useForm<DeleteCompanyFormValues>({
    resolver: zodResolver(deleteCompanySchema),
    defaultValues: {
      reasonDetails: '',
      feedback: '',
      deleteAccount: false,
      confirmName: '',
    },
  })

  const isConfirmed = form.watch('confirmName').trim() === activeCompany?.name

  const handleContinue = async () => {
    const isValid = await form.trigger(['reason', 'reasonDetails'])
    if (isValid) {
      setStep('confirm')
    }
  }

  const onSubmit = (values: DeleteCompanyFormValues) => {
    if (isPending || step !== 'confirm' || !isConfirmed) {
      return
    }

    startTransition(async () => {
      try {
        const res = await cancelSevallaAccount({
          variables: {
            idCompany,
            deleteAccount: isOnlyCompany ? values.deleteAccount : false,
            cancellationReason:
              values.reason === 'Other'
                ? `Other: ${values.reasonDetails}`
                : values.reason,
            cancellationFeedback: values.feedback || undefined,
          },
        })
        const result = res.data?.cancelSevallaAccount

        track('Company Deleted', {
          reason: values.reason,
          hasFeedback: Boolean(values.feedback),
          status:
            result?.status === 'sevallaSubscriptionCancelled'
              ? 'Subscription cancelled'
              : 'Company deletion requested',
          charged: result?.charged,
          forgiven: result?.forgiven,
          accountDeletionRequested: result?.accountDeletionRequested,
        })

        if (result?.status === 'sevallaSubscriptionCancelled') {
          sessionStorage.setItem(SUBSCRIPTION_CANCELLED_KEY, 'true')
          window.location.assign('/')
          return
        }

        const nextCompany = companies.find(
          (company) => company?.id && company.id !== idCompany
        )

        if (nextCompany?.id) {
          localStorage.setItem(SELECTED_COMPANY_KEY, nextCompany.id)
          toast(
            <ToasterContent variant="success">
              Company deleted. Switched to {nextCompany.name}.
            </ToasterContent>,
            { position: 'top-right' }
          )
          navigateToPath({ pathname: '/', idCompany: nextCompany.id })
        } else {
          toast(
            <ToasterContent variant="success">
              Your company has been deleted.
            </ToasterContent>,
            { position: 'top-right' }
          )
          logout?.()
        }
      } catch (error) {
        const message =
          import.meta.env.DEV && error instanceof Error && error.message
            ? error.message
            : 'Cancellation failed. Please try again!'
        toast(<ToasterContent variant="error">{message}</ToasterContent>, {
          position: 'top-right',
        })
      }
    })
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>Delete company</SheetTitle>
        <SheetDescription>
          {isBlocked
            ? 'Remove your projects and services first'
            : STEP_DESCRIPTIONS[step]}
        </SheetDescription>
      </SheetHeader>
      <div className="px-4 overflow-y-auto w-full pb-2">
        {isBlocked ? (
          <BlockedState />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
              {step === 'reason' ? (
                <ReasonStep form={form} />
              ) : (
                <ConfirmStep
                  form={form}
                  companyName={activeCompany?.name ?? ''}
                  isOnlyCompany={isOnlyCompany}
                />
              )}
              <Button type="submit" hidden />
            </form>
          </Form>
        )}
      </div>
      <SheetFooter className="sticky">
        {isBlocked ? (
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        ) : step === 'reason' ? (
          <Button onClick={handleContinue}>Continue</Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => setStep('reason')}>
              Back
            </Button>
            <Button
              variant="destructive"
              disabled={isPending || !isConfirmed}
              onClick={form.handleSubmit(onSubmit)}
            >
              {isPending ? <Loader className="animate-spin" /> : null}
              Delete company
            </Button>
          </>
        )}
      </SheetFooter>
    </>
  )
}

export const DeleteCompanySheet = ({ isOpen, onClose }: Props) => {
  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]" onEscapeKeyDown={onClose}>
        <Suspense fallback={<DeleteCompanySheetFallback />}>
          <Content isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
